import { request } from 'utils/request';
import { header } from 'utils/auth';

const BASE_URL = '/api/v2/myautoiq-settings';

const jsonHeaders = () =>
  new Headers({
    'Content-Type': 'application/json',
    Authorization: header(),
  });

const withMakeId = (url: string, makeId?: string | null): string => {
  if (!makeId) return url;
  return `${url}${url.includes('?') ? '&' : '?'}make_id=${makeId}`;
};

const assertResponseOk = (res: any, fallbackMessage: string) => {
  const status = res?.statusCode ?? res?.myAutoIQ_status_code ?? res?.errorCode;
  const message = res?.message ?? res?.myAutoIQ_error_message;
  if (typeof status === 'number' && status >= 400) {
    throw new Error(message || fallbackMessage);
  }
  return res;
};

interface MakeScopedParams {
  manufacturerId: string;
  makeId?: string | null;
  type: string;
}

export const getSettingsByType = async ({
  manufacturerId,
  makeId,
  type,
}: MakeScopedParams): Promise<any> => {
  const url = withMakeId(
    `${BASE_URL}/manufacturer=${manufacturerId}?type=${type}`,
    makeId,
  );
  const response: any = await request(url, {
    method: 'GET',
    headers: jsonHeaders(),
  });
  if (!response) throw new Error('Failed to fetch myautoiq settings');
  return response;
};

export const getGroupedSettings = async ({
  manufacturerId,
  makeId,
  type,
}: MakeScopedParams): Promise<any> => {
  const url = withMakeId(
    `${BASE_URL}?manufacturer_id=${manufacturerId}&type=${type}`,
    makeId,
  );
  const response: any = await request(url, {
    method: 'GET',
    headers: jsonHeaders(),
  });
  if (!response) throw new Error('Failed to fetch myautoiq settings');
  return response;
};

export const upsertSettingsByType = async ({
  manufacturerId,
  makeId,
  type,
  data,
}: MakeScopedParams & { data: any }): Promise<any> => {
  const url = withMakeId(
    `${BASE_URL}/manufacturer=${manufacturerId}?type=${type}`,
    makeId,
  );
  const response: any = await request(url, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  });
  if (!response) throw new Error('Failed to save myautoiq settings');
  return assertResponseOk(response, 'Failed to save myautoiq settings');
};

export const updateSettingsById = async ({
  manufacturerId,
  makeId,
  settingsId,
  data,
}: {
  manufacturerId: string;
  makeId?: string | null;
  settingsId: string;
  data: any;
}): Promise<any> => {
  const url = withMakeId(
    `${BASE_URL}/manufacturer=${manufacturerId}/${settingsId}`,
    makeId,
  );
  const response: any = await request(url, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  });
  if (!response) throw new Error('Failed to update myautoiq settings');
  return assertResponseOk(response, 'Failed to update myautoiq settings');
};

export const initializeSettings = async ({
  manufacturerId,
  makeId,
  type,
}: MakeScopedParams): Promise<any> => {
  const url = withMakeId(
    `${BASE_URL}/initialize?manufacturer_id=${manufacturerId}&type=${type}`,
    makeId,
  );
  const response: any = await request(url, {
    method: 'POST',
    headers: jsonHeaders(),
  });
  if (!response) throw new Error('Failed to initialize myautoiq settings');
  return assertResponseOk(response, 'Failed to initialize myautoiq settings');
};
